import { useMutation } from '@tanstack/react-query';
import { notifications } from '@mantine/notifications';
import { useTranslation } from 'react-i18next';
import { EICFConsentsApi } from '../../../apis/EICFApi/EICFConsentsApi';
import { QueryKeys } from '../../../queryDefaults/QueryKeys';
import { AppMedError } from '../../../error/AppMedError';

function sanitizePart(input: string): string {
    return input.replace(/[/\\:*?"<>|]/g, '').replace(/\s+/g, '-');
}

/**
 * Exported only so unit tests can pin the filename shape
 * (Consent_<Last>_<First>_<dob>_<generated>_<template>.pdf). The dob segment
 * is omitted entirely when no date of birth is available. The hook is the
 * only production caller.
 */
export function buildPdfFilename(params: {
    patientName?: string | null;
    patientDateOfBirth?: string | null;
    templateName?: string | null;
}): string {
    const nameParts = (params.patientName || 'Unknown').trim().split(/\s+/);
    const lastName =
        nameParts.length > 1 ? nameParts[nameParts.length - 1] : nameParts[0];
    const firstName =
        nameParts.length > 1 ? nameParts.slice(0, -1).join('-') : 'Unknown';
    const dobPart = params.patientDateOfBirth
        ? `${params.patientDateOfBirth.split('T')[0]}_`
        : '';
    const generated = new Date().toISOString().split('T')[0];
    const template = params.templateName || 'Consent';

    const raw = `Consent_${sanitizePart(lastName)}_${sanitizePart(firstName)}_${dobPart}${generated}_${sanitizePart(template)}.pdf`;
    return raw.length > 200 ? raw.substring(0, 196) + '.pdf' : raw;
}

export interface IDownloadEICFConsentPdfParams {
    id: number;
    language: string;
    patientName?: string | null;
    patientDateOfBirth?: string | null;
    templateName?: string | null;
}

export function useDownloadEICFConsentPdf() {
    const { t } = useTranslation();

    return useMutation<
        { blob: Blob; filename: string | null },
        AppMedError,
        IDownloadEICFConsentPdfParams
    >({
        mutationKey: [QueryKeys.eicfSignedPhysicianConsents, 'downloadPdf'],
        mutationFn: async ({ id, language }) => {
            return await EICFConsentsApi.downloadPdf(id, language);
        },
        onSuccess: ({ blob, filename }, params) => {
            EICFConsentsApi.downloadPdfFile(blob, filename || buildPdfFilename(params));

            notifications.show({
                title: t('eicf.notifications.downloadPdfSuccess'),
                message: t('eicf.notifications.downloadPdfSuccessMessage'),
                color: 'green',
            });
        },
        onError: (error: AppMedError) => {
            notifications.show({
                title: t('eicf.notifications.downloadPdfError'),
                message:
                    error?.message ||
                    t('eicf.notifications.downloadPdfErrorMessage'),
                color: 'red',
            });
        },
    });
}
